import classnames from 'classnames';

import style from './DictationButton.module.scss';

import { translate } from '@chaika/i18n/helper';
import MicrophoneIcon from '@components/Icon/svg/microphone.svg';
import { Tooltip, TooltipPlace } from '@components/Tooltip';

import { DictationErrorKind } from './types';

interface Props {
    isDictating: boolean;
    isStarting: boolean;
    error: DictationErrorKind | null;
    hasFailedUtterance: boolean;
    onClick(): void;
}

export function DictationButton({ isDictating, isStarting, error, hasFailedUtterance, onClick }: Props) {
    const tooltipTitle = resolveTooltipTitle({ isDictating, isStarting, error, hasFailedUtterance });

    const isNoticeShown = !!error || hasFailedUtterance;

    return (
        <Tooltip
            title={tooltipTitle}
            place={TooltipPlace.Top}
            isOpen={isNoticeShown ? true : isStarting ? false : undefined}
        >
            <button
                type={'button'}
                data-testid={'dictation-button'}
                className={classnames([
                    style.root,
                    isDictating && !isStarting && style.dictating,
                    isStarting && style.starting,
                    !!error && style.error,
                ])}
                onClick={onClick}
            >
                <MicrophoneIcon className={style.icon} />
            </button>
        </Tooltip>
    );
}

function resolveTooltipTitle({ isDictating, isStarting, error, hasFailedUtterance }: Omit<Props, 'onClick'>): string {
    switch (true) {
        case error !== null:
            return translateError(error);
        case hasFailedUtterance:
            return translate('dictationUtteranceFailed');
        case isStarting:
            return '';
        case isDictating:
            return translate('stopDictation');
        default:
            return translate('voiceInput');
    }
}

function translateError(error: DictationErrorKind): string {
    switch (error) {
        case DictationErrorKind.Microphone:
            return translate('dictationMicrophoneUnavailable');
        case DictationErrorKind.StartFailed:
            return translate('dictationStartFailed');
        case DictationErrorKind.Interrupted:
            return translate('dictationInterrupted');
        case DictationErrorKind.IdleTimeout:
            return translate('dictationStoppedOnSilence');
    }
}
